/**
 * Seller-side calculators: 手取り価格 (case 1) and 売る vs 貸す (case 5).
 *
 * Tax / fee formulas reflect Japanese real-estate practice as of 2026
 * Q1. These are estimates for orientation only — actual figures depend
 * on individual circumstances and should be confirmed with a tax
 * professional before any transaction.
 */

export interface NetProceedsInput {
  /** 売却予定価格 (円). */
  salePrice: number;
  /** 取得価格 (円). Empty / 0 → use the 5% concession rule. */
  acquisitionPrice: number | undefined;
  /** 取得時期 (年). Used to decide 短期 / 長期 / 軽減税率 brackets. */
  acquisitionYear: number | undefined;
  /** 居住用財産だった? (3,000万円特別控除に必要). */
  wasResidence: boolean;
  /** 売却年度 (default = current year). */
  saleYear?: number;
  /** 住宅ローン残債 (円, optional). */
  loanRemaining: number | undefined;
}

export interface NetProceedsBreakdown {
  salePrice: number;
  brokerageFee: number;
  stampDuty: number;
  registrationFee: number;
  capitalGainsTax: number;
  loanRemaining: number;
  netProceeds: number;
  notes: string[];
}

/**
 * 仲介手数料 — the brokerage commission, with the 3% + 6万 standard
 * upper limit, then 10% consumption tax.
 */
function brokerageFee(salePrice: number): number {
  if (salePrice <= 0) return 0;
  let base: number;
  if (salePrice <= 2_000_000) {
    base = salePrice * 0.05;
  } else if (salePrice <= 4_000_000) {
    base = salePrice * 0.04 + 20_000;
  } else {
    base = salePrice * 0.03 + 60_000;
  }
  return Math.round(base * 1.1); // 消費税 10%
}

/**
 * 印紙税 — Stamp Duty on the 売買契約書 (sales contract). 軽減措置 of
 * 2027 Mar 31 applies; bracket figures below are the reduced rates.
 */
function stampDuty(salePrice: number): number {
  if (salePrice <= 1_000_000) return 500;
  if (salePrice <= 5_000_000) return 1_000;
  if (salePrice <= 10_000_000) return 5_000;
  if (salePrice <= 50_000_000) return 10_000;
  if (salePrice <= 100_000_000) return 30_000;
  if (salePrice <= 500_000_000) return 60_000;
  if (salePrice <= 1_000_000_000) return 160_000;
  return 320_000;
}

/**
 * 抵当権抹消の登記費用. Roughly ¥20,000 司法書士 + ¥1,000-2,000 登録
 * 免許税 if there's an outstanding mortgage to release; ¥0 otherwise.
 */
function registrationFee(loanRemaining: number | undefined): number {
  return (loanRemaining ?? 0) > 0 ? 20_000 : 0;
}

/**
 * 譲渡所得税 — Capital-gains tax, applying the 居住用財産 ¥30M deduction
 * if applicable and choosing the 短期 / 長期 / 軽減税率 rate.
 *
 * Simplifications:
 *  - 譲渡費用 is approximated as brokerage + stamp duty.
 *  - When acquisitionPrice is missing or 0, we fall back to 概算取得費
 *    (= sale price × 5%) which is the standard simplification.
 *  - We do not amortise the building portion against the land — that
 *    requires structure metadata we don't ask for. This slightly
 *    overstates the cost basis for older buildings, but is fine as a
 *    seller-side ballpark.
 */
function capitalGainsTax(input: NetProceedsInput, brokerage: number, stamp: number): number {
  const sale = input.salePrice;
  const buy = input.acquisitionPrice && input.acquisitionPrice > 0
    ? input.acquisitionPrice
    : Math.round(sale * 0.05); // 概算取得費 5% rule
  const sellingCost = brokerage + stamp;
  let gain = sale - buy - sellingCost;
  if (gain <= 0) return 0;

  // 居住用財産の 3,000 万円特別控除.
  if (input.wasResidence) {
    gain = Math.max(0, gain - 30_000_000);
  }
  if (gain === 0) return 0;

  // 所有期間判定: 譲渡年の1月1日時点で所有期間 5年超 → 長期, さらに
  // 10年超かつ居住用 → 軽減税率.
  const saleYear = input.saleYear ?? new Date().getFullYear();
  const acqYear = input.acquisitionYear ?? saleYear;
  // 譲渡年の1月1日時点での所有年数 (so we subtract 1 from elapsed)
  const ownedYears = Math.max(0, saleYear - acqYear - 1);
  const isLongTerm = ownedYears >= 5;
  const isVeryLongTerm = ownedYears >= 10 && input.wasResidence;

  let tax: number;
  if (isVeryLongTerm) {
    // 居住用財産 軽減税率: 6,000万以下 14.21% / 超過部 20.315%
    const lower = Math.min(gain, 60_000_000) * 0.1421;
    const upper = Math.max(0, gain - 60_000_000) * 0.20315;
    tax = lower + upper;
  } else if (isLongTerm) {
    tax = gain * 0.20315;
  } else {
    tax = gain * 0.3963;
  }
  return Math.round(tax);
}

export function calculateNetProceeds(input: NetProceedsInput): NetProceedsBreakdown {
  const brokerage = brokerageFee(input.salePrice);
  const stamp = stampDuty(input.salePrice);
  const registration = registrationFee(input.loanRemaining);
  const tax = capitalGainsTax(input, brokerage, stamp);
  const loan = input.loanRemaining ?? 0;
  const net = input.salePrice - brokerage - stamp - registration - tax - loan;
  const notes: string[] = [];
  if (!input.acquisitionPrice || input.acquisitionPrice <= 0) {
    notes.push("取得価格未入力のため、 概算取得費 (売却価格の 5%) で計算しています。");
  }
  if (input.wasResidence) {
    notes.push("居住用財産の 3,000 万円特別控除を適用しています。");
  }
  notes.push("実際の税額は所得・控除等で変動するため、 税理士・税務署への確認を推奨します。");
  return {
    salePrice: input.salePrice,
    brokerageFee: brokerage,
    stampDuty: stamp,
    registrationFee: registration,
    capitalGainsTax: tax,
    loanRemaining: loan,
    netProceeds: net,
    notes,
  };
}

/**
 * Default annual gross rental yield estimates (vs sale price) by
 * property type + prefecture-code prefix. Coarse but enough for a
 * seller-side gut check. Sourced from average 国交省統計 + REINS
 * publications for 2024-2025.
 */
function defaultYieldPercent(
  propertyType: "condo" | "house",
  municipalityCode: string,
): number {
  const pref = (municipalityCode || "").slice(0, 2);
  // Tokyo 23-ku starts with "131"; non-23-ku Tokyo Tama is "132-"/"133-".
  const isTokyo23ku =
    municipalityCode.length === 5 &&
    municipalityCode.startsWith("131") &&
    Number(municipalityCode.slice(3)) <= 23;
  if (propertyType === "condo") {
    if (isTokyo23ku) return 4.0;
    if (pref === "13" || pref === "14") return 4.5; // 都心圏
    if (pref === "27" || pref === "23") return 5.0; // 大阪・愛知
    return 6.0;
  }
  // house
  if (isTokyo23ku) return 3.5;
  if (pref === "13" || pref === "14") return 4.0;
  return 5.5;
}

export interface SellVsRentInput {
  salePrice: number;
  netSaleProceeds: number; // from calculateNetProceeds
  propertyType: "condo" | "house";
  municipalityCode: string;
  holdYears: number;
  /** 想定年間表面利回り (%). Defaults to defaultYieldPercent. */
  grossYieldPercent?: number;
  /** 想定入居率 (0-1). */
  occupancyRate?: number;
}

export interface SellVsRentResult {
  monthlyRent: number;
  annualGrossIncome: number;
  annualNetIncome: number;
  rentTotalNet: number;
  residualPropertyValue: number;
  rentScenarioTotal: number;
  sellScenarioTotal: number;
  delta: number; // 賃貸 − 売却 (正なら賃貸有利)
  yieldUsedPercent: number;
  occupancyUsed: number;
  notes: string[];
}

const ANNUAL_DEPRECIATION = 0.015; // 1.5%/年 — coarse, ignores 構造別
const MGMT_FEE_FRACTION = 0.05; // 5% of rent
const PROPERTY_TAX_FRACTION = 0.014; // 1.4% / year of sale price (proxy for 評価額)
const MAINTENANCE_FRACTION = 0.012; // 1.2% / year of sale price (修繕・大規模修繕積立)
const RENTAL_INCOME_TAX = 0.20; // simplified flat: 不動産所得 × 20%

export function calculateSellVsRent(input: SellVsRentInput): SellVsRentResult {
  const yieldPct =
    input.grossYieldPercent != null
      ? input.grossYieldPercent
      : defaultYieldPercent(input.propertyType, input.municipalityCode);
  const occupancy = input.occupancyRate != null ? input.occupancyRate : 0.9;
  const years = Math.max(1, Math.round(input.holdYears));

  const annualGross = input.salePrice * (yieldPct / 100);
  const monthlyRent = Math.round((annualGross / 12) * occupancy);
  const effectiveAnnualGross = annualGross * occupancy;
  const mgmtFee = effectiveAnnualGross * MGMT_FEE_FRACTION;
  const propTax = input.salePrice * PROPERTY_TAX_FRACTION;
  const maintenance = input.salePrice * MAINTENANCE_FRACTION;
  const taxableIncome = Math.max(0, effectiveAnnualGross - mgmtFee - propTax - maintenance);
  const incomeTax = taxableIncome * RENTAL_INCOME_TAX;
  const annualNet = Math.round(taxableIncome - incomeTax);

  const rentTotalNet = annualNet * years;
  const residual = Math.round(
    input.salePrice * Math.pow(1 - ANNUAL_DEPRECIATION, years),
  );

  const rentScenario = rentTotalNet + residual;
  const sellScenario = input.netSaleProceeds;
  const delta = rentScenario - sellScenario;

  const notes: string[] = [
    `年間表面利回り ${yieldPct.toFixed(1)}% / 入居率 ${(occupancy * 100).toFixed(0)}% で試算。`,
    `修繕・大規模修繕積立を年 ${(MAINTENANCE_FRACTION * 100).toFixed(1)}%、 固定資産税を年 ${(PROPERTY_TAX_FRACTION * 100).toFixed(1)}% と仮定。`,
    `${years} 年後の物件価値は年 ${(ANNUAL_DEPRECIATION * 100).toFixed(1)}% の減価で算出 (構造別の精緻化なし)。`,
    "実際の家賃・経費は物件状態・市場で大きく変動するため、 専門家にご相談ください。",
  ];

  return {
    monthlyRent,
    annualGrossIncome: Math.round(annualGross),
    annualNetIncome: annualNet,
    rentTotalNet,
    residualPropertyValue: residual,
    rentScenarioTotal: rentScenario,
    sellScenarioTotal: sellScenario,
    delta,
    yieldUsedPercent: yieldPct,
    occupancyUsed: occupancy,
    notes,
  };
}
